import './ForceErrorModalContent.css';

import { useRef, useState } from 'react';

import { useTranslate } from '../../../hooks/context/useTranslate';
import { useModalKeyboard } from '../../../hooks/shortcuts/useModalKeyboard';
import { ForceErrorAnswer } from '../../../types/types';
import SeptimLoadingSpinner from '../../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import ModalBody from '../ControlActionModalContent/components/ModalBody/ModalBody';
import RowButton from './components/RowButton/RowButton';

export interface ForceErrorModalContentProps {
  text: string;
  forceModalAction: (value: string) => void | Promise<void>;
  cancelModalAction: () => void | Promise<void>;
  buttons: ForceErrorAnswer[];
}

const ForceErrorModalContent = ({
  text,
  forceModalAction,
  cancelModalAction,
  buttons,
}: ForceErrorModalContentProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const prevActionRef = useRef(forceModalAction);
  const { t } = useTranslate();

  // On chained force errors the same modal instance is reused with a new action
  // instead of remounting. forceModalAction changes per force error, so reset
  // the spinner during render when it does to make the next error's buttons
  // clickable again.
  if (prevActionRef.current !== forceModalAction) {
    prevActionRef.current = forceModalAction;
    setIsSubmitting(false);
  }

  const firstRow = buttons.slice(0, 3);
  const secondRow = buttons.slice(3);

  const handleAction = async (action: () => void | Promise<void>) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await action();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderButton = (answer: ForceErrorAnswer, index: number) => (
    <RowButton
      key={index}
      answer={answer}
      forceModalAction={forceModalAction}
      cancelModalAction={cancelModalAction}
      handleAction={handleAction}
    />
  );

  const primaryAnswers = buttons.filter((answer) => answer.value !== '');
  const cancelAnswer = buttons.find((answer) => answer.value === '');

  useModalKeyboard({
    onEnter:
      primaryAnswers.length === 1
        ? () => handleAction(() => forceModalAction(primaryAnswers[0].value))
        : undefined,
    onEscape: cancelAnswer ? () => handleAction(cancelModalAction) : undefined,
  });

  return (
    <ModalBody
      header={t('warning')}
      text={text}
      buttons={
        isSubmitting ? (
          <SeptimLoadingSpinner className="force-error-modal-spinner" />
        ) : (
          <div className="force-error-modal-buttons">
            <div>{firstRow.map(renderButton)}</div>
            <div>{secondRow.map(renderButton)}</div>
          </div>
        )
      }
    />
  );
};

export default ForceErrorModalContent;
